var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { builder } from '@builder.io/react';
import { getAsyncProps } from '@builder.io/utils';
import { fetchAllCompanySizes, fetchAllRoles, getDataSets, } from './data';
import { ZapCardSectionAsyncProps } from './components';
import { captureBuilderIOKitException } from './init';
export const BUILDER_PUBLIC_API_KEY = process.env.BUILDER_PUBLIC_API_KEY ||
    process.env.NEXT_PUBLIC_BUILDER_PUBLIC_API_KEY;
const getBaseURL = () => {
    var _a;
    if ((_a = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN) === null || _a === void 0 ? void 0 : _a.includes('localhost')) {
        return 'https://zapier-staging.com';
    }
    return process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || 'https://zapier.com';
};
export const constructSearchParams = (options, propertyKey = '') => {
    const searchParams = Object.entries(options).reduce((acc, [key, value]) => {
        const dotNotationKey = propertyKey ? `${propertyKey}.${key}` : key;
        if (typeof value === 'object')
            return [...acc, constructSearchParams(value, dotNotationKey)];
        return [...acc, `${dotNotationKey}=${value}`];
    }, []);
    // remove the trailing `&`
    return searchParams.join('&');
};
const getOptions = (options) => {
    const { options: nestedOptions, userAttributes, url } = options, rest = __rest(options, ["options", "userAttributes", "url"]);
    const urlPath = (userAttributes === null || userAttributes === void 0 ? void 0 : userAttributes.urlPath) || url;
    return Object.assign(Object.assign(Object.assign({}, rest), nestedOptions), { userAttributes: Object.assign(Object.assign({}, userAttributes), { urlPath }) });
};
const fetchBackupContent = (options) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const backupURL = new URL('/api/builder-io-kit/backup', (_a = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN) !== null && _a !== void 0 ? _a : 'https://zapier.com');
    backupURL.search = constructSearchParams(getOptions(options));
    const response = yield fetch(backupURL.toString());
    const content = yield response.json().catch(captureBuilderIOKitException);
    if (response.ok) {
        return content;
    }
    if (response.status !== 404) {
        captureBuilderIOKitException(new Error(`Failed to fetch backup content for ${JSON.stringify(options)}. ${response.status} - ${content.error || 'Unknown error.'}}`));
    }
});
export function fetchEntry(options) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        (_a = options.apiKey) !== null && _a !== void 0 ? _a : (options.apiKey = BUILDER_PUBLIC_API_KEY);
        const _c = getOptions(options), { model } = _c, rest = __rest(_c, ["model"]);
        if (!model) {
            throw new Error('`model` option must be defined');
        }
        const url = new URL(`https://cdn.builder.io/api/v3/content/${model}`);
        url.search = constructSearchParams(rest);
        try {
            const response = yield fetch(url.toString(), {
                headers: { 'Content-Type': 'application/json', accept: '*/*' },
            });
            if (response.ok) {
                const content = yield response.json();
                return Object.assign(Object.assign({}, (_b = content === null || content === void 0 ? void 0 : content.results) === null || _b === void 0 ? void 0 : _b[0]), { model });
            }
            if (response.status !== 404) {
                throw new Error(`API call to Builder.io for ${JSON.stringify(options)} failed. ${response.status} - ${response.statusText}`);
            }
            return { model };
        }
        catch (e) {
            captureBuilderIOKitException(e);
            const content = yield fetchBackupContent(options);
            return Object.assign(Object.assign({}, content), { model });
        }
    });
}
function recursivelyFetchEntries(options, offset = 0, previousEntries = []) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        (_a = options.apiKey) !== null && _a !== void 0 ? _a : (options.apiKey = BUILDER_PUBLIC_API_KEY);
        const entries = (yield builder.getAll(options.model, Object.assign(Object.assign({ cacheSeconds: 0 }, options), { limit: 100, options: Object.assign(Object.assign({}, options.options), { offset }) }))).map((entry) => (Object.assign(Object.assign({}, entry), { model: options.model })));
        if (entries.length === 100) {
            return recursivelyFetchEntries(options, offset + 100, [
                ...previousEntries,
                ...entries,
            ]);
        }
        return [...previousEntries, ...entries];
    });
}
export function fetchAllEntries(options) {
    return __awaiter(this, void 0, void 0, function* () {
        return recursivelyFetchEntries(options);
    });
}
function transformContent(content, propsMapper = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        yield getAsyncProps(content, Object.assign({ ZapCardSection: ZapCardSectionAsyncProps }, propsMapper));
        const dataSets = getDataSets(content);
        let prefetchedData;
        if (dataSets === null || dataSets === void 0 ? void 0 : dataSets.userProfile) {
            const roles = yield fetchAllRoles({ baseURL: getBaseURL() });
            const companySizes = yield fetchAllCompanySizes({ baseURL: getBaseURL() });
            prefetchedData = { roles, companySizes };
        }
        return Object.assign(Object.assign(Object.assign({}, content), (dataSets && { dataSets })), (prefetchedData && { prefetchedData }));
    });
}
export function fetchPage(options, propsMapper) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        if (!options.url && !((_a = options.userAttributes) === null || _a === void 0 ? void 0 : _a.urlPath)) {
            throw new Error('`options.url` or `options.userAttributes.urlPath` must be provided');
        }
        try {
            const page = yield fetchEntry(options);
            return transformContent(page, propsMapper);
        }
        catch (error) {
            captureBuilderIOKitException(error);
            return { model: options.model };
        }
    });
}
export function fetchSection(options, propsMapper) {
    return __awaiter(this, void 0, void 0, function* () {
        const content = yield fetchEntry(Object.assign(Object.assign({}, options), { model: options.model }));
        return transformContent(content, propsMapper);
    });
}
export function fetchAllPaths(options) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const pages = yield fetchAllEntries(Object.assign(Object.assign({}, options), { model: options.model, options: {
                    noTargeting: (_b = (_a = options === null || options === void 0 ? void 0 : options.options) === null || _a === void 0 ? void 0 : _a.noTargeting) !== null && _b !== void 0 ? _b : true,
                } }));
            return pages.reduce((paths, page) => {
                var _a;
                if (!((_a = page === null || page === void 0 ? void 0 : page.data) === null || _a === void 0 ? void 0 : _a.url)) {
                    return paths;
                }
                if (Array.isArray(page.data.url)) {
                    return [...paths, ...page.data.url];
                }
                return [...paths, page.data.url];
            }, []);
        }
        catch (error) {
            captureBuilderIOKitException(error);
            return [];
        }
    });
}
//# sourceMappingURL=fetch.js.map